import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { cn } from "@/lib/cn";
import { isTextEntryTarget } from "@/lib/editable-target";
import {
  DEFAULT_FLYOUT_PLACEMENT,
  submenuFlyoutPlacement,
} from "@/lib/popup-position";
import { usePopupPresence } from "@/hooks/use-popup-presence";
import { useContextMenuPosition } from "./context-menu-position";
import { Icons } from "./icons";
import {
  MenuItemContent,
  menuItemVariants,
  menuSurfaceVariants,
} from "./dropdown-menu";
import { menuSurfaceProps, useMenuSurface } from "./menu-surface";

const FLYOUT_ITEM_SELECTOR = '[role="menuitem"]:not(:disabled)';

interface ContextMenuSubmenuItemProps {
  label: ReactNode;
  icon: ReactNode;
  /** The flyout's accessible name. */
  menuLabel: string;
  /** The flyout's rows; `role="menuitem"` rows take the arrow keys. */
  children: ReactNode;
  disabled?: boolean;
  flyoutClassName?: string;
  onOpenChange?: (open: boolean) => void;
  /** The row; its trigger is `-button`, its flyout `-menu`. */
  "data-testid": string;
}

/**
 * A menu row that opens a flyout beside it: on hover, or from the keyboard
 * (→, Enter, Space) with focus on the flyout's first row. ↑ ↓ Home End move
 * inside the flyout; ← and Escape close it back to the row, so the root menu
 * stays open. A flyout holding a text field focuses it on open and stays open
 * while it is focused, even when the pointer leaves.
 */
export function ContextMenuSubmenuItem({
  label,
  icon,
  menuLabel,
  children,
  disabled = false,
  flyoutClassName,
  onOpenChange,
  "data-testid": testId,
}: ContextMenuSubmenuItemProps) {
  const [open, setOpen] = useState(false);
  const presence = usePopupPresence(open);
  const rowRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const placementRef = useRef<HTMLDivElement>(null);
  const flyoutRef = useRef<HTMLDivElement>(null);
  const openedByKeyboardRef = useRef(false);
  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;
  const [placement, setPlacement] = useState(DEFAULT_FLYOUT_PLACEMENT);
  const menuPosition = useContextMenuPosition();
  // The flyout wears its parent menu's material.
  const material = useMenuSurface();
  const flyoutPaint = menuSurfaceProps(material, {
    nativeLevel: "root",
    nativeGroup: "menus",
  });

  // Measured before paint, again when its rows change (a loading note
  // giving way to the formats) and when the menu around it moves: a flyout
  // near the viewport's right edge opens on the side with more room, one
  // near its bottom moves up, and its first row sits level with this row.
  useLayoutEffect(() => {
    const placed = placementRef.current;
    if (!open || !placed) return;
    const place = () => {
      const row = rowRef.current?.getBoundingClientRect();
      const flyout = flyoutRef.current;
      if (!row || !flyout) return;
      const box = placed.getBoundingClientRect();
      const firstRow = flyout.firstElementChild?.getBoundingClientRect();
      const next = submenuFlyoutPlacement(row, {
        width: box.width,
        height: box.height,
        firstRowInset: firstRow
          ? firstRow.top - flyout.getBoundingClientRect().top
          : 0,
      });
      setPlacement((current) =>
        current.side === next.side &&
        current.offsetX === next.offsetX &&
        current.offsetY === next.offsetY
          ? current
          : next,
      );
    };
    place();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(place);
    observer.observe(placed);
    return () => observer.disconnect();
  }, [open, menuPosition]);

  useEffect(() => {
    if (!open) return;
    const flyout = flyoutRef.current;
    const field = flyout?.querySelector<HTMLInputElement>("input");
    if (field) {
      field.focus();
      field.select();
    } else if (openedByKeyboardRef.current) {
      flyout?.querySelector<HTMLElement>(FLYOUT_ITEM_SELECTOR)?.focus();
    }
    openedByKeyboardRef.current = false;
  }, [open]);

  function setOpenState(next: boolean) {
    if (next === open) return;
    setOpen(next);
    onOpenChangeRef.current?.(next);
  }

  function openFlyout(byKeyboard: boolean) {
    if (disabled) return;
    openedByKeyboardRef.current = byKeyboard;
    setOpenState(true);
  }

  function closeToTrigger() {
    setOpenState(false);
    requestAnimationFrame(() => triggerRef.current?.focus());
  }

  function handleMouseLeave() {
    const active = document.activeElement;
    if (flyoutRef.current?.contains(active) && isTextEntryTarget(active)) {
      return;
    }
    openedByKeyboardRef.current = false;
    setOpenState(false);
  }

  function handleTriggerKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (
      event.key !== "ArrowRight" &&
      event.key !== "Enter" &&
      event.key !== " "
    ) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    openFlyout(true);
  }

  function handleFlyoutKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const typing = isTextEntryTarget(event.target);
    if (event.key === "Escape" || (event.key === "ArrowLeft" && !typing)) {
      event.preventDefault();
      event.stopPropagation();
      closeToTrigger();
      return;
    }
    if (
      typing ||
      (event.key !== "ArrowDown" &&
        event.key !== "ArrowUp" &&
        event.key !== "Home" &&
        event.key !== "End")
    ) {
      return;
    }
    const items = Array.from(
      flyoutRef.current?.querySelectorAll<HTMLElement>(FLYOUT_ITEM_SELECTOR) ??
        [],
    );
    if (items.length === 0) return;
    // The root menu must not move focus back to its own rows.
    event.preventDefault();
    event.stopPropagation();
    const current = items.findIndex((item) => item === document.activeElement);
    let next: number;
    if (event.key === "Home") next = 0;
    else if (event.key === "End") next = items.length - 1;
    else if (event.key === "ArrowDown") {
      next = current < 0 ? 0 : (current + 1) % items.length;
    } else {
      next = current <= 0 ? items.length - 1 : current - 1;
    }
    items[next]?.focus();
  }

  return (
    <div
      ref={rowRef}
      className="relative"
      onMouseEnter={() => openFlyout(false)}
      onMouseLeave={handleMouseLeave}
      data-testid={testId}
    >
      <button
        ref={triggerRef}
        type="button"
        role="menuitem"
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => openFlyout(false)}
        onKeyDown={handleTriggerKeyDown}
        className={cn(
          menuItemVariants({ density: "roomy" }),
          "w-full text-left focus:outline-none focus-visible:text-primary",
          // Glass rows keep the heavy glass fill; the wall's card menus use
          // the card's hover line. The row stays lit while its flyout is open.
          material === "card"
            ? "focus-visible:bg-[var(--color-interactive-hover)] aria-expanded:bg-[var(--color-interactive-hover)]"
            : "focus-visible:bg-glass-fill-heavy aria-expanded:bg-glass-fill-heavy",
          disabled
            ? "cursor-not-allowed text-muted opacity-60"
            : material === "card"
              ? "text-primary hover:bg-[var(--color-interactive-hover)]"
              : "text-primary hover:bg-glass-fill-heavy",
        )}
        data-testid={`${testId}-button`}
      >
        <MenuItemContent
          icon={icon}
          trailing={disabled ? null : <Icons.ChevronRight />}
        >
          {label}
        </MenuItemContent>
      </button>
      {presence.mounted && !disabled && (
        <div
          ref={placementRef}
          className={cn(
            // Sized by its own items, not by the zero room past the row's edge.
            "absolute z-[10000] w-max",
            placement.side === "left"
              ? "right-full pr-appkit-xs"
              : "left-full pl-appkit-xs",
          )}
          style={{
            top: placement.offsetY,
            transform: placement.offsetX
              ? `translateX(${placement.offsetX}px)`
              : undefined,
          }}
          data-side={placement.side}
        >
          <div
            ref={flyoutRef}
            className={cn(
              flyoutPaint.className,
              menuSurfaceVariants({ surface: material }),
              flyoutClassName,
            )}
            data-state={presence.state}
            role="menu"
            aria-label={menuLabel}
            onKeyDown={handleFlyoutKeyDown}
            data-testid={`${testId}-menu`}
            {...flyoutPaint.attributes}
          >
            {children}
          </div>
        </div>
      )}
    </div>
  );
}
